import React, { useState } from 'react';
import { createMemoStyles, defaultBaseline, uniqueId, safeRest } from '@wds/utils';
import { Label } from '@wds/label';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var _assign = function __assign() {
  _assign = Object.assign || function __assign(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) {
        if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
      }
    }
    return t;
  };
  return _assign.apply(this, arguments);
};
function __rest(s, e) {
  var t = {};
  for (var p in s) {
    if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  }
  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
}
var useStyles = createMemoStyles(function (theme) {
  var spacing = theme.spacing,
    motion = theme.motion,
    shadow = theme.shadow,
    palette = theme.palette,
    border = theme.border,
    radius = theme.radius;
  var switchComp = {
    track: {
      bgColorOn: palette.muted.contrast,
      bgColorOff: palette.muted.main
    },
    thumb: {
      bgColorOn: palette.primary.main,
      bgColorOff: palette.primary.contrast,
      borderOff: border.main
    },
    borderRadius: radius.medium,
    height: 16,
    leftOffset: 20,
    width: 40
  };
  return {
    wrenchSwitch: function wrenchSwitch() {
      return {
        display: 'inline-block',
        position: 'relative',
        verticalAlign: 'middle',
        minHeight: switchComp.height
      };
    },
    wrenchSwitchNative: function wrenchSwitchNative() {
      return {
        position: 'absolute',
        cursor: 'inherit',
        opacity: 0,
        width: '100%',
        height: '100%',
        zIndex: 1,
        left: 0,
        top: 0,
        '&:focus + $wrenchSwitchFaux': {
          outline: '5px auto -webkit-focus-ring-color',
          fallbacks: {
            outline: '1px dotted #212121'
          }
        },
        '&:checked + $wrenchSwitchFaux': {
          borderColor: switchComp.track.bgColorOn,
          background: switchComp.track.bgColorOn,
          '&:after': _assign(_assign({}, defaultBaseline), {
            borderColor: switchComp.thumb.bgColorOn,
            background: switchComp.thumb.bgColorOn,
            left: switchComp.leftOffset
          })
        },
        '&:disabled + $wrenchSwitchFaux': {
          opacity: '0.5',
          transform: 'scale(1)'
        }
      };
    },
    wrenchSwitchFaux: function wrenchSwitchFaux() {
      return {
        display: 'block',
        height: switchComp.height,
        width: switchComp.width,
        background: switchComp.track.bgColorOff,
        borderRadius: switchComp.borderRadius,
        transition: 'background linear 50ms',
        // Handle
        '&:after': {
          content: "''",
          display: 'block',
          boxSizing: 'border-box',
          padding: 0,
          margin: 0,
          position: 'relative',
          left: 0,
          top: -spacing[1],
          height: spacing[5],
          width: spacing[5],
          borderRadius: switchComp.borderRadius,
          background: switchComp.thumb.bgColorOff,
          border: switchComp.thumb.borderOff,
          transition: "left ".concat(motion.curve.ease, " 150ms, background linear 50ms"),
          boxShadow: shadow.sky
        }
      };
    }
  };
});
var Switch = function Switch(_a) {
  var ariaLabel = _a.ariaLabel,
    checked = _a.checked,
    _b = _a.defaultChecked,
    defaultChecked = _b === void 0 ? false : _b,
    _c = _a.disabled,
    disabled = _c === void 0 ? false : _c,
    id = _a.id,
    innerRef = _a.innerRef,
    label = _a.label,
    onChange = _a.onChange,
    rest = __rest(_a, ["ariaLabel", "checked", "defaultChecked", "disabled", "id", "innerRef", "label", "onChange"]);
  var _d = useState(!!defaultChecked),
    isChecked = _d[0],
    setChecked = _d[1];
  var _e = useState(false),
    isFocused = _e[0],
    setFocused = _e[1];
  var checkedState = checked !== null && checked !== void 0 ? checked : isChecked;
  var switchId = id !== null && id !== void 0 ? id : uniqueId('input');
  var classes = useStyles();
  var handleFocus = function handleFocus(e) {
    setFocused(!isFocused);
    e.persist();
  };
  var handleChange = function handleChange(e) {
    setChecked(!checkedState);
    e.persist();
    if (onChange) onChange(e);
  };
  return React.createElement("div", {
    className: classes.wrenchSwitch
  }, label && React.createElement(Label, {
    htmlFor: switchId
  }, label), React.createElement("input", _assign({
    "aria-label": ariaLabel,
    onFocus: handleFocus,
    onBlur: handleFocus,
    role: "switch",
    "aria-checked": checkedState,
    checked: checkedState,
    className: classes.wrenchSwitchNative,
    disabled: disabled,
    id: switchId,
    onChange: handleChange,
    tabIndex: disabled ? -1 : 0,
    type: "checkbox",
    ref: innerRef
  }, safeRest(rest))), React.createElement("span", {
    className: classes.wrenchSwitchFaux
  }));
};
export { Switch };